/* ==========================================================================
   sj-suede.js — Shirtology · Suede Jacket landing page.

   Loaded only by sections/sj-suede-landing.liquid. No dependencies, no build
   step, no framework: this page is opened on mid-range Android inside the
   Facebook and Instagram in-app browsers, where every parsed kilobyte is time
   the visitor spends looking at nothing.

   Ported from assets/st-stripe.js, which drives the live stripe page. The
   conventions here are ITS conventions on purpose — `.sj-rv` + `data-in`,
   `data-open="true"`/`"false"` written literally rather than toggled by
   presence, `data-show` on the toast and the dock, `[data-prog]` scaled by
   transform, `--i` grouped per row — because the same stylesheet shape is
   being written against both pages. Its comments record bugs that actually
   happened; they are kept, adapted, not paraphrased away.

   Everything degrades without JS. The page is fully readable and every link
   works; this file only adds the language toggle, the scroll reveal, the size
   sheet, the bag and the thumb-reach dock.
   ========================================================================== */

(function () {
  'use strict';

  var root = document.getElementById('sj-root');
  if (!root) return;

  /* Kept for feature styling only. Reveal visibility no longer depends on it —
     see the reveal block below for why that inversion mattered. */
  document.documentElement.classList.remove('no-js');

  /* ------------------------------------------------------------------------
     Meta ViewContent.

     Shopify's web pixel cannot fire this — product_viewed only runs on a
     product template and this is a page. See the note on the pixel base code
     in layout/sj-suede.liquid for why that matters to the Suede Jacket DPA.

     content_ids are BARE variant ids, because that is what `retailer_id` is in
     the catalog this store's DPA pulls from (verified 2026-07-26; see
     cloudflare/yellowlemon-meta.js). One representative IN-STOCK variant per
     colourway rather than every size, so the signal is not buried.

     `value` is the lowest price a buyer can actually pay. Out-of-stock chips
     are skipped for both, or the page would report a price nobody can buy at.
     Prices are minor units in the markup, hence /100 — getting that wrong is a
     100x error in every reported conversion value.
     ---------------------------------------------------------------------- */
  (function () {
    if (!window.sjTrack) return;

    var cards = root.querySelectorAll('[data-card]');
    var ids = [];
    var lowest = 0;

    for (var i = 0; i < cards.length; i++) {
      var chips = cards[i].querySelectorAll('[data-variant]');
      var picked = '';
      for (var j = 0; j < chips.length; j++) {
        if (chips[j].getAttribute('data-out') === 'true') continue;
        var price = Number(chips[j].getAttribute('data-price')) || 0;
        if (price > 0 && (lowest === 0 || price < lowest)) lowest = price;
        if (!picked) picked = chips[j].getAttribute('data-variant') || '';
      }
      if (picked) ids.push(String(picked));
    }

    if (!ids.length) return;

    window.sjTrack('ViewContent', {
      content_type: 'product',
      content_ids: ids,
      content_name: 'Suede Finish Jacket',
      content_category: 'Suede Jacket',
      value: lowest / 100,
      currency: 'BDT'
    });
  })();

  /* ------------------------------------------------------------------------
     Bangla copy.

     English is NOT duplicated here — it is harvested from the rendered DOM at
     boot, so the markup stays the single source of truth for it and the two can
     never drift. Only Bangla lives in this table.

     `{n}` is the live colourway count and `{in}` / `{out}` the delivery
     charges. They are substituted at apply time from data attributes Liquid
     wrote onto #sj-root, so a number can never be baked into a translation and
     then quietly go stale — which is exactly how a page ends up advertising a
     count or a delivery charge that is no longer true.

     The suede runs S–6XL since the October 2026 restock; every size claim in
     both languages says so. Prices above XXL are a dearer tier and are never
     typed here — each size chip carries its own variant price.
     ---------------------------------------------------------------------- */
  var BN = {
    'nav.shop': 'শপ',

    'hero.eyebrow': 'নিউ অ্যারাইভাল',
    'hero.h1a': 'প্রিমিয়াম',
    'hero.h1b': 'সোয়েড',
    'hero.h1c': 'জ্যাকেট',
    'hero.lead': '{n}টি কালারওয়েতে সফট সোয়েড ফিনিশ। ভেতরে ফুল লাইনিং, স্মুথ মেটাল জিপার, আর রিব কাফ ও হেম — ঢাকার শীতের জন্য ঠিক যতটা দরকার, তার চেয়ে ভারী নয়।',
    'hero.from': 'শুরু',
    'hero.cta': 'সব কালার দেখুন',
    'hero.cta2': 'সাইজ চার্ট',
    'hero.t1': 'সফট সোয়েড ফিনিশ',
    'hero.t2': 'S — 6XL',
    'hero.t3': 'ক্যাশ অন ডেলিভারি',
    'hero.off': '{off} ছাড়',

    'cw.eyebrow': 'কালারওয়ে',
    'cw.h2': '{n}টি কালারওয়ে থেকে বেছে নিন',
    'cw.lead': 'প্রতিটি জ্যাকেটই আলাদা একটি কালার — সাইজ বেছে নিতে যেকোনোটিতে ট্যাপ করুন।',
    'cw.off': 'ছাড়',
    'cw.out': 'স্টক শেষ',

    'q.eyebrow': 'কনস্ট্রাকশন',
    'q.h2': 'এক্সপোর্ট অর্ডারের মতো করে তৈরি',
    'q.f1h': 'সফট সোয়েড ফিনিশ',
    'q.f1p': 'হাতে ধরলেই পার্থক্যটা বোঝা যায় — ম্যাট, নরম, আর সস্তা সিনথেটিকের মতো চকচকে নয়।',
    'q.f2h': 'ফুল ইনার লাইনিং',
    'q.f2p': 'ভেতরটা পুরো লাইন করা, তাই সোয়েড সরাসরি গায়ে লাগে না আর জ্যাকেটটা শেপে থাকে।',
    'q.f3h': 'স্মুথ মেটাল জিপার',
    'q.f3p': 'জিপার আটকে যায় না, আর ফুল-লেংথ চালালেও কাপড় টেনে ধরে না।',
    'q.f4h': 'রিব কাফ ও হেম',
    'q.f4p': 'হাতা আর কোমরে রিব ফিনিশ, যাতে বাতাস ঢুকতে না পারে আর ধোয়ার পরেও ঢিলে হয়ে না যায়।',
    'q.f5h': 'ইউটিলিটি পকেট',
    'q.f5p': 'বাইরে দুটি হাত-পকেট, ভেতরে একটি — ফোন আর মানিব্যাগ দুটোই নিরাপদে ধরে।',
    'q.f6h': 'যেকোনো অকেশনে',
    'q.f6p': 'অফিস, ক্যাজুয়াল হ্যাংআউট, কিংবা এর মাঝের যেকোনো উপলক্ষ — শার্টের উপরেও চলে, টি-শার্টের উপরেও।',

    'qr.h': 'নিজেই যাচাই করে নিন',
    'qr.p': 'প্রতিটি জ্যাকেটে রয়েছে একটি কিউআর কোড। স্ক্যান করলেই আপনি সেই জ্যাকেটের নিজস্ব পেজে চলে যাবেন আমাদের ওয়েবসাইটে — যাতে আপনি নিশ্চিত হতে পারেন, যা কিনেছেন সেটাই আমরা পাঠিয়েছি।',

    'sz.eyebrow': 'সাইজ',
    'sz.h2': 'S থেকে 6XL, চেস্ট ৪০″ থেকে ৫৬″',
    'sz.lead': 'মাপগুলো জ্যাকেটের নিজের, ইঞ্চিতে দেওয়া। সব কালারে সব সাইজ নেই — উপরের প্রতিটি কার্ডে এখন কোন সাইজগুলো স্টকে আছে তা দেওয়া রয়েছে।',
    'sz.c0': 'সাইজ',
    'sz.c1': 'চেস্ট',
    'sz.c2': 'লম্বা',
    'sz.c3': 'দাম',
    'sz.hint': 'সব মাপ দেখতে টেবিলটি পাশে স্ক্রল করুন →',

    'close.h': 'অর্ডার করতে প্রস্তুত?',
    'close.p': 'সারা বাংলাদেশে ক্যাশ অন ডেলিভারি। ঢাকার ভেতরে ডেলিভারি {in}, ঢাকার বাইরে {out}।',
    'close.cta': 'কালার বেছে নিন',
    'close.cta2': 'সব জ্যাকেট দেখুন',

    'sheet.eyebrow': 'আপনার পছন্দ',
    'sheet.size': 'সাইজ',
    'sheet.qty': 'পরিমাণ',
    'sheet.name': 'পুরো নাম',
    'sheet.phone': 'মোবাইল নম্বর',
    'sheet.addr': 'সম্পূর্ণ ঠিকানা',
    'sheet.city': 'শহর / জেলা',
    'sheet.area': 'ডেলিভারি এলাকা',
    'sheet.inside': 'ঢাকার ভেতরে',
    'sheet.outside': 'ঢাকার বাইরে',
    'sheet.note': 'ডেলিভারি চার্জ চেকআউটে যোগ হবে। ক্যাশ অন ডেলিভারি রয়েছে।',
    'sheet.go': 'চেকআউটে যান',
    'sheet.chart': 'সাইজ চার্ট',
    'sheet.chartnote': 'জ্যাকেটের নিজের মাপ, ইঞ্চিতে।',
    'sheet.about': 'প্রোডাক্ট ডিটেইলস',
    'sheet.line': 'লাইন টোটাল',
    'sheet.add': 'ব্যাগে যোগ করুন',
    'sheet.buy': 'এখনই কিনুন',
    'sheet.out': 'স্টক শেষ',

    'bag.title': 'আপনার ব্যাগ',
    'bag.empty': 'আপনার ব্যাগ খালি।',
    'bag.browse': 'কালারওয়ে দেখুন',
    'bag.details': 'ডেলিভারির তথ্য',
    'bag.sub': 'সাবটোটাল',
    'bag.ship': 'ডেলিভারি',
    'bag.total': 'সর্বমোট',
    'bag.go': 'চেকআউট',
    'bag.go2': 'অর্ডার নিশ্চিত করুন',
    'bag.note': 'ক্যাশ অন ডেলিভারি রয়েছে। চেকআউটে Free Delivery বেছে নিয়ে অনলাইনে পেমেন্ট করলে ডেলিভারি একদম ফ্রি।',
    'bag.size': 'সাইজ',

    'dock.inbag': 'আপনার ব্যাগে',
    'dock.view': 'ব্যাগ দেখুন',
    'toast.added': 'ব্যাগে যোগ হয়েছে',
    'toast.removed': 'সরানো হয়েছে',

    'sheet.errSize': 'একটি সাইজ বেছে নিন।',
    'sheet.errName': 'আপনার নাম লিখুন।',
    'sheet.errPhone': 'সঠিক মোবাইল নম্বর দিন (যেমন ০১৭XXXXXXXX)।',
    'sheet.errAddr': 'সম্পূর্ণ ঠিকানা লিখুন।',
    'sheet.errCity': 'শহর বা জেলার নাম লিখুন।',

    'app.eyebrow': 'শার্টোলজি অ্যাপ',
    'app.h': 'অ্যাপে প্রতিটি অর্ডারে ১৫% ছাড়',
    'app.p': 'ছাড়টি নিজে থেকেই যোগ হয়ে যায় — কোনো কোড লিখতে হয় না। সেই সাথে রিয়েল-টাইম কুরিয়ার ট্র্যাকিং, রিওয়ার্ড পয়েন্ট যা ভাউচার হিসেবে খরচ করা যায়, আর অনলাইনে পেমেন্ট করলে ফ্রি ডেলিভারি।',
    'app.off': 'ছাড়',
    'app.gp1': 'পাবেন',
    'app.gp2': 'Google Play',
    'app.as1': 'ডাউনলোড করুন',
    'app.as2': 'App Store',

    'foot.android': 'অ্যান্ড্রয়েড অ্যাপ',
    'foot.ios': 'আইফোন অ্যাপ',
    'foot.home': 'হোম',
    'foot.shop': 'সব জ্যাকেট',
    'foot.cart': 'কার্ট',

    'mq.1': 'সফট সোয়েড ফিনিশ',
    'mq.2': 'ফুল ইনার লাইনিং',
    'mq.3': 'স্মুথ মেটাল জিপার',
    'mq.4': 'রিবড কলার, কাফ ও হেম',
    'mq.5': 'S থেকে 6XL',

    'dock.lab': 'সোয়েড জ্যাকেট',
    // Added after live verification 2026-09-07: these five keys existed in the
    // markup but had no Bangla, so a bn reader saw English inside an otherwise
    // fully translated page — and two of them sit on the offer, which is this
    // page's entire hook.
    'hero.tbig': 'সাইজ চার্ট দেখুন',
    'sz.bignote': 'সব সাইজেই ছাড় — 3XL ও তার বড় সাইজের দাম আলাদা স্তরের, প্রতিটির ছাড়ের দাম উপরে দেওয়া আছে।',
    'sz.biglink': 'আপনার কালার বেছে নিন →',
    'vid.eyebrow': 'ভিডিও',
    'vid.h2': 'কাছ থেকে দেখুন',
    'vid.lead': 'সব কালার, ফেব্রিক আর ফিটিং — সাউন্ডের জন্য ভিডিওতে ট্যাপ করুন।',
    'vid.lab1': 'পুরো ভিডিও',
    'vid.lab2': '২৫ সেকেন্ডে',
    'vid.lab3': '২৫ সেকেন্ড · প্রাণবন্ত',
    'offer.save': 'সেভ করছেন',
    'offer.off': 'ছাড়',
    'offer.note': 'চেকআউটে এই দামই দিতে হবে — কোনো কোড লিখতে হবে না।',
    'dock.choose': 'বেছে নিন',
    'mq.6': 'ক্যাশ অন ডেলিভারি',
    'dock.cta': 'বেছে নিন'
  };

  /* English is normally harvested from the rendered DOM, so the markup stays
     its single source of truth. These strings are the exception: they are only
     ever shown by the script, so they appear in no element to harvest from —
     without them the sheet renders the raw i18n key at a validation error. */
  var EN = {
    'sheet.errSize': 'Choose a size first.',
    'sheet.errName': 'Please enter your name.',
    'sheet.errPhone': 'Enter a valid Bangladeshi mobile number (e.g. 017XXXXXXXX).',
    'sheet.errAddr': 'Please enter your full address.',
    'sheet.errCity': 'Please enter your city or district.',
    'sheet.out': 'Sold out',
    'bag.size': 'Size',
    'bag.go2': 'Place order',
    'dock.inbag': 'In your bag',
    'dock.view': 'View bag',
    'toast.added': 'Added to your bag',
    'toast.removed': 'Removed'
  };

  /* Secondary index, English text -> Bangla, consulted only when a key has no
     entry in BN above. Three agents built this page in parallel against a
     written contract and could not talk to each other; if the section ended up
     keying a string differently from the table above, this still translates it
     instead of leaving a Bangla page with an English paragraph in the middle.
     Never overrides BN — it is the net, not the rule. */
  var BN_BY_EN = {
    'shop': 'শপ',
    'suede jacket': 'সোয়েড জ্যাকেট',
    'suede jackets': 'সোয়েড জ্যাকেট',
    'colourways': 'কালারওয়ে',
    'colours': 'কালার',
    'size': 'সাইজ',
    'sizes': 'সাইজ',
    'size chart': 'সাইজ চার্ট',
    'size guide': 'সাইজ চার্ট',
    'chest': 'চেস্ট',
    'length': 'লম্বা',
    'quantity': 'পরিমাণ',
    'delivery': 'ডেলিভারি',
    'inside dhaka': 'ঢাকার ভেতরে',
    'outside dhaka': 'ঢাকার বাইরে',
    'cash on delivery': 'ক্যাশ অন ডেলিভারি',
    'add to bag': 'ব্যাগে যোগ করুন',
    'buy now': 'এখনই কিনুন',
    'checkout': 'চেকআউট',
    'place order': 'অর্ডার নিশ্চিত করুন',
    'your bag': 'আপনার ব্যাগ',
    'your bag is empty.': 'আপনার ব্যাগ খালি।',
    'your bag is empty': 'আপনার ব্যাগ খালি।',
    'browse colourways': 'কালারওয়ে দেখুন',
    'delivery details': 'ডেলিভারির তথ্য',
    'subtotal': 'সাবটোটাল',
    'total': 'সর্বমোট',
    'full name': 'পুরো নাম',
    'mobile number': 'মোবাইল নম্বর',
    'full address': 'সম্পূর্ণ ঠিকানা',
    'city / district': 'শহর / জেলা',
    'sold out': 'স্টক শেষ',
    'out of stock': 'স্টক শেষ',
    'remove': 'সরান',
    'close': 'বন্ধ করুন',
    'from': 'শুরু',
    'new arrival': 'নিউ অ্যারাইভাল',
    'off': 'ছাড়'
  };

  function normEn(s) {
    return String(s == null ? '' : s).replace(/\s+/g, ' ').trim().toLowerCase();
  }

  var lang = 'en';

  var COUNT = root.getAttribute('data-count') || '';
  var OFF = root.getAttribute('data-off') || '';

  var BN_DIGITS = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'];

  function toBn(str) {
    return String(str).replace(/[0-9]/g, function (d) { return BN_DIGITS[+d]; });
  }

  /* ------------------------------------------------------------------------
     Money.

     No currency symbol is written in this file. The shape comes from one
     sample Liquid rendered through the shop's own money_format, so a store
     that stops printing "Tk" does not need this script edited.

     The sample is a KNOWN amount — the section renders `100000 | money…`, the
     way the stripe page does. That makes the divisor derivable instead of
     assumed: a shop formatting 100000 as "Tk 1,000" is holding minor units, a
     shop formatting it as "Tk 100,000" is not. Guessing wrong is what would
     silently price a Tk 2,690 jacket at Tk 26.90.
     ---------------------------------------------------------------------- */
  var SAMPLE = root.getAttribute('data-money-sample') || 'Tk 1,000';
  var mm = SAMPLE.match(/^(.*?)[\d.,]+(.*)$/);
  var MPRE = mm ? mm[1] : '';
  var MPOST = mm ? mm[2] : '';
  var SAMPLE_N = Number(String(SAMPLE).replace(/[^0-9]/g, '')) || 0;
  var DIVISOR = SAMPLE_N >= 100000 ? 1 : 100;

  /* Grouping separator taken from the same sample, so a shop on a dot-grouped
     money_format is not rewritten into commas by this file. */
  var GROUP = /\d\.\d{3}(\D|$)/.test(SAMPLE) ? '.' : ',';

  function groupInt(n) {
    var s = String(n);
    var out = '';
    for (var i = s.length - 1, c = 0; i >= 0; i--) {
      out = s.charAt(i) + out;
      if (++c % 3 === 0 && i > 0) out = GROUP + out;
    }
    return out;
  }

  /* Latin form. Everything the script writes into the page goes through
     setNum() below, which needs the un-converted string to remember. */
  function moneyRaw(cents) {
    return MPRE + groupInt(Math.round(cents / DIVISOR)) + MPOST;
  }

  function money(cents) {
    var out = moneyRaw(cents);
    return lang === 'bn' ? toBn(out) : out;
  }

  /* Write a number into a node and keep its `data-num-en` in step.
     applyLang() repaints every [data-bn-num] from that attribute, so a value
     this script produced would otherwise be reverted to whatever Liquid
     rendered at boot the moment the visitor switches language — the bag badge
     snapping back to the count the page loaded with, the total to blank, both
     until the next render happened to run. */
  function setNum(el, latin) {
    if (!el) return;
    if (el.hasAttribute('data-bn-num')) el.setAttribute('data-num-en', latin);
    el.textContent = lang === 'bn' ? toBn(latin) : latin;
  }

  var SHIP_IN_N = Number(root.getAttribute('data-ship-in-n')) || 0;
  var SHIP_OUT_N = Number(root.getAttribute('data-ship-out-n')) || 0;

  /* Formatted delivery strings for the prose. Liquid may hand them over
     ready-made the way the stripe section does; if it does not, they are
     formatted from the same taka figures the bag does arithmetic on, so the
     sentence and the total can never disagree. */
  function shipText(attr, taka) {
    var given = root.getAttribute(attr);
    if (given) return lang === 'bn' ? toBn(given) : given;
    return money(taka * 100);
  }

  function fill(str) {
    return str
      .replace(/\{n\}/g, lang === 'bn' ? toBn(COUNT) : COUNT)
      .replace(/\{off\}/g, lang === 'bn' ? toBn(OFF) : OFF)
      .replace(/\{in\}/g, shipText('data-ship-in', SHIP_IN_N))
      .replace(/\{out\}/g, shipText('data-ship-out', SHIP_OUT_N));
  }

  /* Harvest the rendered English once, before anything has been swapped. */
  function harvest() {
    var nodes = root.querySelectorAll('[data-i18n], [data-i18n-html]');
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      var htmlKey = n.getAttribute('data-i18n-html');
      var key = htmlKey || n.getAttribute('data-i18n');
      if (key && EN[key] == null) EN[key] = htmlKey ? n.innerHTML : n.textContent;
    }
    /* Numerals: remember the Latin original so switching back is lossless. */
    var nums = root.querySelectorAll('[data-bn-num]');
    for (var j = 0; j < nums.length; j++) {
      nums[j].setAttribute('data-num-en', nums[j].textContent);
    }
  }

  /* Bangla for a key: the table first, then the English-text net. Returns null
     when neither has it, so the caller falls back to the harvested English —
     a readable English string beats a raw key on a Bangla page. */
  function bnFor(key) {
    if (BN[key] != null) return BN[key];
    var en = EN[key];
    if (en != null) {
      var hit = BN_BY_EN[normEn(en)];
      if (hit != null) return hit;
    }
    return null;
  }

  function applyLang() {
    var nodes = root.querySelectorAll('[data-i18n], [data-i18n-html]');
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      var htmlKey = n.getAttribute('data-i18n-html');
      var key = htmlKey || n.getAttribute('data-i18n');
      if (!key) continue;
      var val = lang === 'bn' ? bnFor(key) : null;
      if (val == null) val = EN[key];
      if (val == null) continue;
      val = fill(val);
      if (htmlKey) n.innerHTML = val; else n.textContent = val;
    }

    var nums = root.querySelectorAll('[data-bn-num]');
    for (var j = 0; j < nums.length; j++) {
      var orig = nums[j].getAttribute('data-num-en');
      if (orig == null) continue;
      nums[j].textContent = lang === 'bn' ? toBn(orig) : orig;
    }

    document.documentElement.setAttribute('data-lang', lang);

    var btns = root.querySelectorAll('[data-lang-btn]');
    for (var k = 0; k < btns.length; k++) {
      btns[k].setAttribute('aria-pressed', btns[k].getAttribute('data-lang-btn') === lang ? 'true' : 'false');
    }
  }

  /* Set by the bag below. applyLang() rewrites every [data-i18n] node from the
     harvested table, which includes labels the bag owns the state of — the
     Checkout button carries data-i18n="bag.go" but reads "Place order" once
     step two is open. Re-rendering on a deferred tick let that revert be
     visible; running it as part of the switch does not. */
  var afterLang = null;

  function setLang(next) {
    lang = next === 'bn' ? 'bn' : 'en';
    applyLang();
    if (afterLang) afterLang();
    try { localStorage.setItem('sj-lang', lang); } catch (e) {}
  }

  harvest();

  root.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest('[data-lang-btn]') : null;
    if (!btn) return;
    setLang(btn.getAttribute('data-lang-btn'));
  });

  var saved = 'en';
  try { saved = localStorage.getItem('sj-lang') || 'en'; } catch (e) {}
  setLang(saved === 'bn' ? 'bn' : 'en');

  /* ------------------------------------------------------------------------
     Reveal on scroll. Skipped entirely for reduced-motion visitors and where
     IntersectionObserver is missing — in both cases everything is shown at
     once rather than left invisible, which is the failure that would make the
     page look empty.
     ---------------------------------------------------------------------- */
  var reveals = root.querySelectorAll('.sj-rv');
  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Hiding is opt-IN, applied here, and only to what is genuinely off screen.

     This used to work the other way round: the stylesheet hid every .sj-rv and
     <html class="no-js"> exempted them until this script removed that class.
     The effect was that the moment the deferred script ran, everything below
     the fold went blank and only came back as the observer caught up — and a
     visitor scrolling during that window saw a page with nothing on it but the
     nav. On a slow connection that window is seconds long.

     Now: anything already at or near the viewport is marked arrived and NEVER
     hidden, so what you can see cannot blank. Only off-screen elements get the
     hidden flag, and each is handed to the observer in the same pass, so
     nothing is ever hidden without something watching to reveal it. */
  if (!('IntersectionObserver' in window) || reduced) {
    for (var r = 0; r < reveals.length; r++) reveals[r].setAttribute('data-in', 'skip');
  } else {
    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (!entries[i].isIntersecting) continue;
        entries[i].target.removeAttribute('data-hide');
        entries[i].target.setAttribute('data-in', 'true');
        io.unobserve(entries[i].target);
      }
    }, { rootMargin: '300px 0px 300px 0px', threshold: 0 });

    var vh = window.innerHeight || 800;
    for (var m = 0; m < reveals.length; m++) {
      var el = reveals[m];
      var box = el.getBoundingClientRect();
      if (box.top < vh * 1.6) {
        /* on screen, or about to be — show it and leave it alone */
        el.setAttribute('data-in', 'true');
      } else {
        el.setAttribute('data-hide', '');
        io.observe(el);
      }
    }
  }

  /* ------------------------------------------------------------------------
     Nav border + mobile dock, both driven off one scroll listener.

     The dock appears once the hero's CTA has scrolled away, so it never
     duplicates a button already on screen.
     ---------------------------------------------------------------------- */
  var nav = root.querySelector('[data-nav]');
  var dockEl = root.querySelector('[data-dock]');
  var heroCta = root.querySelector('.sj-hero__cta');
  var ticking = false;

  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(function () {
      var y = window.pageYOffset || document.documentElement.scrollTop;
      if (nav) nav.setAttribute('data-stuck', y > 8 ? 'true' : 'false');

      if (dockEl) {
        var past = true;
        if (heroCta) past = heroCta.getBoundingClientRect().bottom < 0;
        /* Hide again at the very bottom so it never sits on the final CTA. */
        var atEnd = (window.innerHeight + y) >= (document.body.scrollHeight - 140);
        dockEl.setAttribute('data-show', past && !atEnd ? 'true' : 'false');
      }
      ticking = false;
    });
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  onScroll();

  /* ------------------------------------------------------------------------
     Theme.

     No stored value means "follow the OS", which is the state the page ships
     in — the layout stamps data-theme before first paint ONLY when the visitor
     has chosen one, and this file must not stamp it otherwise or the page
     would stop following a mid-session OS change. Reading the CURRENT computed
     state before flipping is what makes the first tap always do the visible
     thing, whichever way the OS happens to be set.
     ---------------------------------------------------------------------- */
  var themeBtn = root.querySelector('[data-theme-btn]');

  function currentTheme() {
    var forced = document.documentElement.getAttribute('data-theme');
    if (forced === 'dark' || forced === 'light') return forced;
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  /* The status-bar colour is read back out of the stylesheet rather than
     duplicated here, so a palette change in the CSS cannot leave the phone's
     chrome on last season's colour. EVERY theme-color meta is rewritten and
     each one's `media` attribute removed: the layout ships a light/dark pair
     gated on prefers-color-scheme, and an override that keeps `media` stays
     inert against an explicit choice — the status bar would go on following
     the OS after the visitor had already overruled it. */
  function paintThemeColor(theme) {
    var cs = window.getComputedStyle(root);
    var c = (cs.getPropertyValue('--sj-bg') || '').trim();
    if (!c) c = cs.backgroundColor;
    if (!c || c === 'transparent' || c === 'rgba(0, 0, 0, 0)') {
      c = window.getComputedStyle(document.body).backgroundColor;
    }
    if (!c) c = theme === 'dark' ? '#12151b' : '#f7f6f2';
    var metas = document.querySelectorAll('meta[name="theme-color"]');
    for (var i = 0; i < metas.length; i++) {
      metas[i].setAttribute('content', c);
      metas[i].removeAttribute('media');
    }
  }

  if (themeBtn) {
    themeBtn.setAttribute('aria-pressed', currentTheme() === 'dark' ? 'true' : 'false');
    themeBtn.addEventListener('click', function () {
      var next = currentTheme() === 'dark' ? 'light' : 'dark';
      document.documentElement.setAttribute('data-theme', next);
      try { localStorage.setItem('sj-theme', next); } catch (e) {}
      themeBtn.setAttribute('aria-pressed', next === 'dark' ? 'true' : 'false');
      /* Synchronous on purpose: reading getComputedStyle forces the style
         flush, so --sj-bg already resolves against the attribute written on
         the line above. Deferring to a frame only left the status bar on the
         old colour for one. */
      paintThemeColor(next);
    });
  }

  /* ------------------------------------------------------------------------
     Hero colourway cycle.

     Two <img> layers, not one per colourway: the next image is loaded into
     whichever layer is hidden and the two swap. That ships one image instead of
     five, which is the difference between a fast hero and a 1 MB one on a
     Bangladeshi mobile connection.

     Auto-advance stops permanently the moment the visitor picks a chip — once
     they are steering, moving it under them is just rude. It also pauses while
     the hero is off screen, and never starts at all under reduced-motion.
     ---------------------------------------------------------------------- */
  var stack = root.querySelector('[data-stack]');
  var chips = [].slice.call(root.querySelectorAll('[data-sw]'));
  var tag = root.querySelector('[data-hero-code]');

  if (stack && chips.length) {
    var layers = [stack.querySelector('[data-layer="a"]'), stack.querySelector('[data-layer="b"]')];
    var front = 0;
    var idx = 0;
    var timer = null;
    var userPicked = false;
    var visible = true;
    var DWELL = 3600;

    chips[0].setAttribute('data-on', 'true');

    var preload = function (i) {
      var c = chips[i];
      if (!c) return;
      var im = new Image();
      im.decoding = 'async';
      im.src = c.getAttribute('data-src');
    };

    var show = function (i, viaUser) {
      if (i === idx) return;
      var chip = chips[i];
      if (!chip) return;
      var src = chip.getAttribute('data-src');
      var back = layers[1 - front];
      if (!back) return;

      var apply = function () {
        layers[front].classList.remove('is-on');
        back.classList.add('is-on');
        front = 1 - front;
        idx = i;
        for (var k = 0; k < chips.length; k++) {
          if (k === i) chips[k].setAttribute('data-on', 'true');
          else chips[k].removeAttribute('data-on');
        }
        if (tag) tag.textContent = chip.getAttribute('data-code') || '';
        preload((i + 1) % chips.length);
      };

      if (back.getAttribute('src') === src && back.complete) { apply(); return; }
      stack.setAttribute('data-loading', 'true');
      /* Swap only once the bytes have arrived, so a slow connection never
         flashes a blank hero — and on an error leave the picture that IS on
         screen alone rather than swapping to a broken one. */
      back.onload = function () { stack.removeAttribute('data-loading'); apply(); };
      back.onerror = function () { stack.removeAttribute('data-loading'); };
      back.setAttribute('src', src);
      back.setAttribute('alt', chip.getAttribute('data-code') || '');
      if (viaUser) back.removeAttribute('aria-hidden');
    };

    var stopCycle = function () { if (timer) { clearInterval(timer); timer = null; } };

    var startCycle = function () {
      if (reduced || userPicked || timer || chips.length < 2) return;
      timer = setInterval(function () {
        if (!visible) return;
        show((idx + 1) % chips.length);
      }, DWELL);
    };

    var warmed = {};
    var warm = function (i) {
      if (warmed[i]) return;
      warmed[i] = 1;
      preload(i);
    };

    for (var ci = 0; ci < chips.length; ci++) {
      (function (i) {
        var c = chips[i];
        c.addEventListener('click', function (e) {
          e.preventDefault();      // the href is the no-JS fallback only
          userPicked = true;
          stopCycle();
          show(i, true);
        });
        /* Warm on first touch, not on tap: by the time the finger lifts the
           bytes are already on their way and the swap feels instant. */
        c.addEventListener('touchstart', function () { warm(i); }, { passive: true });
        c.addEventListener('pointerenter', function () { warm(i); });
      })(ci);
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (es) {
        visible = es[0].isIntersecting;
      }, { threshold: 0.15 }).observe(stack);
    }

    preload(1);
    startCycle();
  }

  /* ------------------------------------------------------------------------
     Stagger + scroll progress.

     `--i` is the element's position within its own group, so a row of cards
     arrives as a run instead of all at once. Grouped by parent, otherwise the
     last card on the page would wait a second and a half for its turn.
     ---------------------------------------------------------------------- */
  var groups = {};
  for (var g = 0; g < reveals.length; g++) {
    var par = reveals[g].parentNode;
    var gkey = par.className || 'x';
    groups[gkey] = groups[gkey] || 0;
    /* Capped at 4, not 8. At the old 70ms step an index of 8 meant 560ms of dead
       time before a card even started to fade, which is why a four-up grid row
       could read as three cards missing and one present. */
    reveals[g].style.setProperty('--i', Math.min(groups[gkey], 4));
    groups[gkey]++;
  }

  var prog = root.querySelector('[data-prog]');
  if (prog) {
    var progTick = false;
    var onProg = function () {
      if (progTick) return;
      progTick = true;
      window.requestAnimationFrame(function () {
        var h = document.documentElement.scrollHeight - window.innerHeight;
        var y = window.pageYOffset || document.documentElement.scrollTop;
        prog.style.transform = 'scaleX(' + (h > 0 ? Math.min(1, y / h) : 0) + ')';
        progTick = false;
      });
    };
    window.addEventListener('scroll', onProg, { passive: true });
    window.addEventListener('resize', onProg, { passive: true });
    onProg();
  }

  /* ------------------------------------------------------------------------
     The bag.

     A real multi-line cart held in localStorage, so several colourways and
     sizes can be assembled in one order and survive a reload. Checkout is a
     single Shopify cart permalink carrying every line —
     /cart/<v1>:<q1>,<v2>:<q2>?checkout[...] — which lands the buyer on the
     store's REAL checkout with the whole bag and their address prefilled.

     That route is deliberate. Building the order here instead (the way the
     Yellow Lemon worker pages must, having no Shopify checkout to hand off to)
     would bypass every Shopify Function the store depends on: COD hidden on
     Free Delivery, "Inside Dhaka" hidden outside Dhaka, and phone/country
     validation.
     ---------------------------------------------------------------------- */
  var sheet = root.querySelector('[data-sheet]');
  var bag   = root.querySelector('[data-bag]');

  if (sheet && bag) {
    var CART_KEY = 'sj-cart';
    var AREA_KEY = 'sj-area';
    var MAX_QTY = 10;

    /* Same rule the validate-phone-by-country Function enforces at checkout.
       Anything looser here only moves the rejection to a screen the buyer
       reaches believing they are finished. */
    var BD_PHONE = /^(\+?880|0)1[3-9]\d{8}$/;

    /* ---- state ---- */

    function readCart() {
      var out = [];
      try {
        var raw = JSON.parse(localStorage.getItem(CART_KEY));
        if (Array.isArray(raw)) {
          out = raw.filter(function (l) { return l && l.variant && l.qty > 0; });
        }
      } catch (e) {}
      return out;
    }

    var cart = readCart();

    function persist() {
      try { localStorage.setItem(CART_KEY, JSON.stringify(cart)); } catch (e) {}
    }

    function count() {
      var n = 0;
      for (var i = 0; i < cart.length; i++) n += cart[i].qty;
      return n;
    }

    function subtotal() {
      var t = 0;
      for (var i = 0; i < cart.length; i++) t += cart[i].price * cart[i].qty;
      return t;
    }

    function addLine(line) {
      for (var i = 0; i < cart.length; i++) {
        if (cart[i].variant === line.variant) {
          cart[i].qty = Math.min(MAX_QTY, cart[i].qty + line.qty);
          persist();
          return;
        }
      }
      cart.push(line);
      persist();
    }

    /* ---- elements ---- */

    var elBagN     = bag.querySelector('[data-bag-n]');
    var elLines    = bag.querySelector('[data-bag-lines]');
    var elEmpty    = bag.querySelector('[data-bag-empty]');
    var elFoot     = bag.querySelector('[data-bag-foot]');
    var elCta      = bag.querySelector('[data-bag-cta]');
    var elSub      = bag.querySelector('[data-bag-sub]');
    var elShip     = bag.querySelector('[data-bag-ship]');
    var elTotal    = bag.querySelector('[data-bag-total]');
    var elGo       = bag.querySelector('[data-bag-go]');
    var elBagErr   = bag.querySelector('[data-bag-err]');
    var elForm     = bag.querySelector('[data-order-form]');
    var badge      = root.querySelector('[data-bag-count]');
    var toastEl    = root.querySelector('[data-toast]');
    var dockLab    = root.querySelector('[data-dock-lab]');
    var dockAmt    = root.querySelector('[data-dock-amt]');
    var dockCta    = root.querySelector('[data-dock-cta]');

    var area = 'inside';
    try { if (localStorage.getItem(AREA_KEY) === 'outside') area = 'outside'; } catch (e) {}
    var toastT = null;

    function toast(msg) {
      if (!toastEl) return;
      toastEl.textContent = msg;
      toastEl.setAttribute('data-show', 'true');
      window.clearTimeout(toastT);
      toastT = window.setTimeout(function () { toastEl.setAttribute('data-show', 'false'); }, 2200);
    }

    function T(key) {
      if (lang === 'bn') {
        var bn = bnFor(key);
        if (bn != null) return fill(bn);
      }
      return fill(EN[key] != null ? EN[key] : key);
    }

    /* ---- render ---- */

    function renderBag() {
      var n = count();

      if (badge) {
        setNum(badge, String(n));
        badge.hidden = n === 0;
      }
      setNum(elBagN, String(n));

      if (elEmpty) elEmpty.hidden = n > 0;
      if (elFoot) elFoot.hidden = n === 0;
      if (elCta) elCta.hidden = n === 0;
      if (elForm && n === 0) elForm.hidden = true;

      /* The Checkout button carries data-i18n="bag.go", so a language switch
         would otherwise reset it to "Checkout" while step two is open in front
         of the buyer. Its label is re-derived from the step every render, and
         renderBag is what the language toggle calls. An emptied bag lands back
         on step one for the same reason. */
      if (elGo) {
        elGo.disabled = false;
        elGo.textContent = (elForm && !elForm.hidden) ? T('bag.go2') : T('bag.go');
      }

      /* The dock stops advertising a starting price once there is a bag to
         finish — at that point the useful action is checkout, not browsing. */
      if (dockLab && dockAmt && dockCta) {
        if (n > 0) {
          dockLab.textContent = T('dock.inbag');
          setNum(dockAmt, String(n) + ' · ' + moneyRaw(subtotal()));
          dockCta.textContent = T('dock.view');
          dockCta.setAttribute('data-open-bag', '1');
        } else {
          dockCta.removeAttribute('data-open-bag');
        }
      }

      var segs = bag.querySelectorAll('[data-area]');
      for (var s = 0; s < segs.length; s++) {
        segs[s].setAttribute('aria-pressed', segs[s].getAttribute('data-area') === area ? 'true' : 'false');
      }

      if (!elLines) return;
      elLines.innerHTML = '';

      for (var i = 0; i < cart.length; i++) {
        var l = cart[i];
        var li = document.createElement('li');
        li.className = 'sj-line';
        li.setAttribute('data-line', l.variant);
        /* The static skeleton only. Nothing derived from stored data goes
           through innerHTML — not the text, and not the image URL either,
           which is the one that would otherwise carry an attribute straight
           out of localStorage into markup. */
        li.innerHTML =
          '<img class="sj-line__img" alt="" width="58" height="73" loading="lazy">' +
          '<span class="sj-line__mid">' +
            '<span class="sj-line__code"></span>' +
            '<span class="sj-line__sz"></span>' +
            '<span class="sj-line__p"></span>' +
          '</span>' +
          '<span class="sj-line__right">' +
            '<button type="button" class="sj-line__rm" data-rm aria-label="Remove">' +
              '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>' +
            '</button>' +
            '<span class="sj-line__qty">' +
              '<button type="button" class="sj-line__qb" data-lq="-1" aria-label="Decrease">&minus;</button>' +
              '<span class="sj-line__qn"></span>' +
              '<button type="button" class="sj-line__qb" data-lq="1" aria-label="Increase">+</button>' +
            '</span>' +
          '</span>';
        li.querySelector('.sj-line__img').src = l.img || '';
        li.querySelector('.sj-line__code').textContent = l.code;
        li.querySelector('.sj-line__sz').textContent = T('bag.size') + ' ' + l.size;
        setNum(li.querySelector('.sj-line__p'), moneyRaw(l.price * l.qty));
        setNum(li.querySelector('.sj-line__qn'), String(l.qty));
        elLines.appendChild(li);
      }

      var sub = subtotal();
      var ship = (area === 'outside' ? SHIP_OUT_N : SHIP_IN_N) * 100;
      setNum(elSub, moneyRaw(sub));
      setNum(elShip, moneyRaw(ship));
      setNum(elTotal, moneyRaw(sub + ship));
    }

    function openBag() {
      /* The open bag IS the confirmation, and on a phone the toast lands right
         on top of the checkout button. Stand it down. */
      if (toastEl) toastEl.setAttribute('data-show', 'false');
      window.clearTimeout(toastT);
      bag.setAttribute('data-open', 'true');
      document.body.style.overflow = 'hidden';
      renderBag();
    }

    function closeBag() {
      bag.setAttribute('data-open', 'false');
      document.body.style.overflow = '';
    }

    /* ---- size sheet ---- */

    var sBody  = sheet.querySelector('[data-sheet-sizes]');
    var sCode  = sheet.querySelector('[data-sheet-code]');
    var gMain  = sheet.querySelector('[data-gal-main]');
    var gStrip = sheet.querySelector('[data-gal-strip]');
    var sTotal = sheet.querySelector('[data-sheet-total]');
    var sNow   = sheet.querySelector('[data-sheet-now]');
    var sWas   = sheet.querySelector('[data-sheet-was]');
    var sPct   = sheet.querySelector('[data-sheet-pct]');
    var sSave  = sheet.querySelector('[data-sheet-save]');
    var sOffer = sheet.querySelector('[data-sheet-offer]');
    var sErr   = sheet.querySelector('[data-sheet-err]');
    var qtyN   = sheet.querySelector('[data-qty-n]');

    var sel = null;
    var qty = 1;
    var lastFocus = null;

    /* The sheet's price line and badges follow the chosen size. Sizes above
       XXL are a dearer tier with their own compare-at, and showing the
       headline 2,690 saving over a 2,890 price would read as the discount
       having gone — so each size states its own pair. */
    function sheetPrice(now, was) {
      if (!now) return;
      setNum(sNow, moneyRaw(now));
      var on = was > now;
      if (sWas) { sWas.hidden = !on; if (on) setNum(sWas, moneyRaw(was)); }
      if (sOffer) sOffer.hidden = !on;
      if (on) {
        setNum(sPct, String(Math.round((was - now) * 100 / was)));
        setNum(sSave, moneyRaw(was - now));
      }
    }

    function sheetTotal() {
      setNum(sTotal, sel ? moneyRaw(sel.price * qty) : '—');
      setNum(qtyN, String(qty));
    }

    function sheetErr(key) {
      if (!sErr) return false;
      sErr.textContent = T(key);
      sErr.hidden = false;
      return false;
    }

    function openSheet(card) {
      lastFocus = document.activeElement;
      sel = null; qty = 1;
      if (sErr) sErr.hidden = true;

      if (sCode) sCode.textContent = card.getAttribute('data-code') || '';
      sheet.setAttribute('data-img', card.getAttribute('data-img') || '');

      /* Gallery. The strip is only shown when there is genuinely more than one
         shot — a strip of a single thumbnail reads as broken rather than as a
         choice. */
      var imgs = (card.getAttribute('data-images') || '')
        .split('|')
        .map(function (u) { return u.trim(); })
        .filter(Boolean);
      if (!imgs.length) imgs = [card.getAttribute('data-img')].filter(Boolean);

      if (gMain && imgs.length) gMain.setAttribute('src', imgs[0]);
      if (gMain) gMain.setAttribute('alt', card.getAttribute('data-code') || '');

      if (gStrip) {
        gStrip.innerHTML = '';
        if (imgs.length > 1) {
          for (var g2 = 0; g2 < imgs.length; g2++) {
            var tb = document.createElement('button');
            tb.type = 'button';
            tb.className = 'sj-gal__t';
            tb.setAttribute('aria-pressed', g2 === 0 ? 'true' : 'false');
            tb.setAttribute('data-gsrc', imgs[g2]);
            tb.setAttribute('aria-label', 'View image ' + (g2 + 1));
            var im2 = document.createElement('img');
            im2.src = imgs[g2];
            im2.alt = '';
            im2.loading = 'lazy';
            tb.appendChild(im2);
            gStrip.appendChild(tb);
          }
          gStrip.hidden = false;
        } else {
          gStrip.hidden = true;
        }
      }

      sBody.innerHTML = '';
      /* Sizes come from the card's own variant chips, so a size is disabled
         here because Liquid found it out of stock at render — not because this
         file carries a list a restock would make wrong — which is why nothing
         here needs to know the size ladder at all. */
      var vchips = card.querySelectorAll('[data-variant]');
      for (var i = 0; i < vchips.length; i++) {
        var c = vchips[i];
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'sj-sizes__b';
        b.textContent = c.getAttribute('data-size');
        b.setAttribute('aria-pressed', 'false');
        /* Accept either convention for "out": the attribute carrying "true",
           or simply being present. */
        if (c.hasAttribute('data-out') && c.getAttribute('data-out') !== 'false') {
          b.disabled = true;
          b.setAttribute('aria-label', c.getAttribute('data-size') + ' — ' + T('sheet.out'));
        }
        b.setAttribute('data-v', c.getAttribute('data-variant'));
        b.setAttribute('data-p', c.getAttribute('data-price'));
        b.setAttribute('data-w', c.getAttribute('data-compare') || '0');
        b.setAttribute('data-s', c.getAttribute('data-size'));
        sBody.appendChild(b);
      }

      /* Until a size is picked, the line shows the colourway's lowest price. */
      var firstOk = sBody.querySelector('.sj-sizes__b:not(:disabled)');
      if (firstOk) sheetPrice(Number(firstOk.getAttribute('data-p')) || 0, Number(firstOk.getAttribute('data-w')) || 0);
      sheetTotal();
      sheet.setAttribute('data-open', 'true');
      document.body.style.overflow = 'hidden';
      var first = sBody.querySelector('.sj-sizes__b:not(:disabled)');
      if (first) window.setTimeout(function () { first.focus(); }, 220);
    }

    function closeSheet() {
      sheet.setAttribute('data-open', 'false');
      document.body.style.overflow = '';
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    function commit() {
      if (!sel) return sheetErr('sheet.errSize');
      var code = sCode ? sCode.textContent : '';
      addLine({
        variant: sel.variant,
        size: sel.size,
        price: sel.price,
        qty: qty,
        code: code,
        img: sheet.getAttribute('data-img') || ''
      });

      /* Meta AddToCart. addLine() has one caller and commit() has two, both of
         them the same deliberate tap ("add to bag" / "buy now"), so this fires
         exactly once per add and cannot be double counted.

         Shopify's web pixel never sees it: the bag is localStorage until the
         /cart/<v>:<q> hand-off, so no cart AJAX call happens and
         product_added_to_cart never runs.

         value is major units; sel.price is minor, like everything Liquid prints
         on this page. */
      if (window.sjTrack) {
        window.sjTrack('AddToCart', {
          content_type: 'product',
          content_ids: [String(sel.variant)],
          contents: [{ id: String(sel.variant), quantity: qty }],
          content_name: code || 'Suede Finish Jacket',
          value: (sel.price * qty) / 100,
          currency: 'BDT'
        });
      }

      renderBag();
      return true;
    }

    /* Cards are real links; intercept only the plain click, so modified and
       middle clicks still open the product page. */
    root.addEventListener('click', function (e) {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button) return;
      var card = e.target.closest ? e.target.closest('[data-card]') : null;
      if (card) { e.preventDefault(); openSheet(card); return; }

      if (e.target.closest('[data-bag-open]')) { openBag(); return; }
      var dk = e.target.closest('[data-open-bag]');
      if (dk) { e.preventDefault(); openBag(); }
    });

    sheet.addEventListener('click', function (e) {
      var t = e.target;
      if (t.closest('[data-sheet-close]')) { closeSheet(); return; }

      var sz = t.closest('.sj-sizes__b');
      if (sz && !sz.disabled) {
        var all = sBody.querySelectorAll('.sj-sizes__b');
        for (var i = 0; i < all.length; i++) all[i].setAttribute('aria-pressed', 'false');
        sz.setAttribute('aria-pressed', 'true');
        sel = { variant: sz.getAttribute('data-v'), size: sz.getAttribute('data-s'), price: Number(sz.getAttribute('data-p')) || 0 };
        sheetPrice(sel.price, Number(sz.getAttribute('data-w')) || 0);
        if (sErr) sErr.hidden = true;
        sheetTotal();
        return;
      }

      var gt = t.closest('[data-gsrc]');
      if (gt) {
        if (gMain) gMain.setAttribute('src', gt.getAttribute('data-gsrc'));
        var ts = gStrip.querySelectorAll('[data-gsrc]');
        for (var i2 = 0; i2 < ts.length; i2++) ts[i2].setAttribute('aria-pressed', ts[i2] === gt ? 'true' : 'false');
        return;
      }

      var qb = t.closest('[data-qty]');
      if (qb) { qty = Math.max(1, Math.min(MAX_QTY, qty + Number(qb.getAttribute('data-qty')))); sheetTotal(); return; }

      if (t.closest('[data-sheet-add]')) {
        if (commit()) { closeSheet(); toast(T('toast.added')); }
        return;
      }
      if (t.closest('[data-sheet-buy]')) {
        if (commit()) { closeSheet(); openBag(); }
      }
    });

    /* ---- bag interactions ---- */

    bag.addEventListener('click', function (e) {
      var t = e.target;
      if (t.closest('[data-bag-close]')) { closeBag(); return; }
      if (t.closest('[data-bag-browse]')) { closeBag(); scrollToColourways(); return; }

      var li = t.closest('[data-line]');
      if (li) {
        var vid = li.getAttribute('data-line');
        var lidx = -1;
        for (var i = 0; i < cart.length; i++) if (cart[i].variant === vid) lidx = i;
        if (lidx < 0) return;

        if (t.closest('[data-rm]')) {
          cart.splice(lidx, 1);
          persist(); renderBag();
          toast(T('toast.removed'));
          return;
        }
        var lq = t.closest('[data-lq]');
        if (lq) {
          var next = cart[lidx].qty + Number(lq.getAttribute('data-lq'));
          if (next < 1) cart.splice(lidx, 1);
          else cart[lidx].qty = Math.min(MAX_QTY, next);
          persist(); renderBag();
          return;
        }
      }

      var ar = t.closest('[data-area]');
      if (ar) {
        area = ar.getAttribute('data-area') === 'outside' ? 'outside' : 'inside';
        /* Remembered, because someone outside Dhaka is outside Dhaka on their
           next visit too, and a total that silently reverts to the cheaper
           rate reads as a bait. */
        try { localStorage.setItem(AREA_KEY, area); } catch (e2) {}
        renderBag();
        return;
      }

      if (t.closest('[data-bag-go]')) checkout();
    });

    function scrollToColourways() {
      var target = document.getElementById('colourways');
      if (target) target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth' });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (bag.getAttribute('data-open') === 'true') { closeBag(); return; }
      if (sheet.getAttribute('data-open') === 'true') closeSheet();
    });

    /* ---- checkout ---- */

    function val(name) {
      var f = bag.querySelector('[data-f="' + name + '"]');
      return f ? f.value.trim() : '';
    }

    function mark(name, bad) {
      var f = bag.querySelector('[data-f="' + name + '"]');
      if (f) {
        f.setAttribute('aria-invalid', bad ? 'true' : 'false');
        if (bad) f.focus();
      }
    }

    function bagErr(key) {
      if (!elBagErr) return false;
      elBagErr.textContent = T(key);
      elBagErr.hidden = false;
      return false;
    }

    function checkout() {
      if (!cart.length) return;
      if (elBagErr) elBagErr.hidden = true;

      /* Step one: reveal the details. Asking for an address before anyone has
         committed to checking out is where a landing page loses people. */
      if (elForm && elForm.hidden) {
        elForm.hidden = false;
        elGo.textContent = T('bag.go2');
        var f1 = elForm.querySelector('[data-f]');
        if (f1) {
          elForm.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'nearest' });
          window.setTimeout(function () { f1.focus(); }, reduced ? 0 : 320);
        }
        return;
      }

      var name = val('name');
      if (name.length < 2) { mark('name', true); return bagErr('sheet.errName'); }
      mark('name', false);

      var phone = val('phone').replace(/[\s-]/g, '');
      if (!BD_PHONE.test(phone)) { mark('phone', true); return bagErr('sheet.errPhone'); }
      mark('phone', false);

      var addr = val('address');
      if (addr.length < 8) { mark('address', true); return bagErr('sheet.errAddr'); }
      mark('address', false);

      var city = val('city');
      if (city.length < 2) { mark('city', true); return bagErr('sheet.errCity'); }
      mark('city', false);

      elGo.disabled = true;

      var parts = [];
      for (var i = 0; i < cart.length; i++) parts.push(cart[i].variant + ':' + cart[i].qty);

      var bits = name.split(/\s+/);
      var qs = [];
      var add = function (k, v) { if (v) qs.push(encodeURIComponent(k) + '=' + encodeURIComponent(v)); };
      var sp = 'checkout[shipping_address]';

      /* A lone name goes in LAST name, not first: orderCreate silently discards
         a MailingAddressInput whose lastName is empty, taking the phone and the
         whole address down with it. */
      add(sp + '[first_name]', bits.length > 1 ? bits[0] : '');
      add(sp + '[last_name]', bits.length > 1 ? bits.slice(1).join(' ') : name);
      add(sp + '[address1]', addr);
      add(sp + '[city]', city);
      add(sp + '[country]', 'Bangladesh');
      add(sp + '[phone]', phone);
      add('attributes[Delivery area]', area === 'outside' ? 'Outside Dhaka' : 'Inside Dhaka');
      add('attributes[Source]', 'Suede landing');

      /* The bag has done its job once the buyer is on checkout; leaving it
         populated means a back-button lands them on a page insisting they still
         have an order to finish. */
      cart = [];
      try { localStorage.removeItem(CART_KEY); } catch (e) {}

      window.location.href = '/cart/' + parts.join(',') + (qs.length ? '?' + qs.join('&') : '');
    }

    /* Money, every numeral and the step-dependent labels have to re-render
       when the language flips. */
    afterLang = function () { renderBag(); sheetTotal(); };

    /* Another tab finished an order, or added to the same bag. Re-read rather
       than let two copies of this page drift apart and then fight over which
       one gets written last. */
    window.addEventListener('storage', function (e) {
      if (!e || e.key !== CART_KEY) return;
      cart = readCart();
      renderBag();
    });

    renderBag();
    sheetTotal();
  }

  /* ------------------------------------------------------------------------
     Snowfall.

     Generated here rather than written into the Liquid so the count follows the
     viewport: a 320px phone gets a third of what a desktop does, because the
     same density that reads as atmosphere on a wide screen reads as a blizzard
     on a narrow one — and every flake is an animated element a mid-range
     Android has to composite every frame.

     Nothing is generated at all under reduced-motion, and the whole layer is
     detached when the tab is hidden. Decoration must not cost battery in the
     background.
     ---------------------------------------------------------------------- */
  /* ------------------------------------------------------------------------
     The video band — the boxer page's player (st-stripe.js), ported. ONE
     frame plays at a time: two streams decoding side by side dropped ~6% of
     their frames on a throttled phone (measured on the boxer page), and a
     mid-range Android is slower than that. preload="none" in the markup, so
     nothing past the posters is fetched until play() is called here.
     ---------------------------------------------------------------------- */
  (function () {
    var wraps = root.querySelectorAll('[data-vid]');
    if (!wraps.length) return;
    var auto = !reduced && 'IntersectionObserver' in window;
    var current = null;   /* the wrap that is playing */
    var loud = null;      /* the <video> that has sound */
    var ratios = [];
    var held = [];        /* paused by a tap: the observer must not restart it */

    function elOf(w) { return w.querySelector('[data-vid-el]'); }
    function manual(w) {
      if (w.getAttribute('data-vid-manual') === 'true') return;
      w.setAttribute('data-vid-manual', 'true');
      elOf(w).controls = true;
    }
    function setSound(w, on) {
      var el = elOf(w), btn = w.querySelector('[data-vid-sound]');
      el.muted = !on;
      if (btn) {
        btn.setAttribute('aria-pressed', on ? 'false' : 'true');
        btn.setAttribute('aria-label', on ? 'Turn sound off' : 'Turn sound on');
      }
      if (on) loud = el; else if (loud === el) loud = null;
    }
    function stop(w) {
      var el = elOf(w);
      if (!el.paused) el.pause();
      if (loud === el) setSound(w, false);
    }
    function start(w) {
      if (current && current !== w) stop(current);
      current = w;
      var el = elOf(w), p;
      try { p = el.play(); } catch (e) { manual(w); return; }
      if (p && typeof p.catch === 'function') p.catch(function () { manual(w); });
    }
    function pick() {
      var best = 0.5, win = null;
      for (var i = 0; i < wraps.length; i++) {
        if (held[i]) continue;
        if (ratios[i] > best) { best = ratios[i]; win = wraps[i]; }
      }
      /* Keep whatever is playing while it is still well on screen, so the
         arbiter does not flip between two equally visible frames. */
      if (current) {
        var ci = Array.prototype.indexOf.call(wraps, current);
        if (ratios[ci] >= 0.5 && !held[ci]) return;
        stop(current);
        current = null;
      }
      if (win) start(win);
    }

    Array.prototype.forEach.call(wraps, function (w, i) {
      var el = elOf(w), btn = w.querySelector('[data-vid-sound]');
      if (!el) return;
      ratios[i] = 0; held[i] = false;
      if (!auto) manual(w);
      w.addEventListener('click', function (e) {
        if (e.target.closest && e.target.closest('[data-vid-sound]')) return;
        if (el.paused) { held[i] = false; start(w); }
        else { held[i] = true; stop(w); if (current === w) current = null; }
      });
      if (btn) btn.addEventListener('click', function () {
        var on = el.muted;
        if (on && loud && loud !== el) {
          for (var k = 0; k < wraps.length; k++) if (elOf(wraps[k]) === loud) setSound(wraps[k], false);
        }
        setSound(w, on);
        /* Turning the sound on restarts from the top, so the visitor hears
           the pitch from its first word, not mid-sentence. */
        if (on) {
          try { el.currentTime = 0; } catch (e2) {}
          held[i] = false;
          start(w);
        }
      });
    });

    if (auto) {
      var io = new IntersectionObserver(function (es) {
        for (var i = 0; i < es.length; i++) {
          var idx = Array.prototype.indexOf.call(wraps, es[i].target);
          if (idx >= 0) ratios[idx] = es[i].intersectionRatio;
        }
        pick();
      }, { threshold: [0, 0.5, 0.9] });
      Array.prototype.forEach.call(wraps, function (w) { io.observe(w); });
    }

    /* Hidden tab, or the hand-off to checkout: stop, and say nothing. */
    document.addEventListener('visibilitychange', function () {
      if (document.hidden && current) stop(current);
    });
  })();

  (function snowfall() {
    if (reduced) return;

    var host = document.createElement('div');
    host.className = 'sj-snow';
    host.setAttribute('aria-hidden', 'true');
    host.setAttribute('data-snow', '');

    var PLANES = [
      /* Slower, smaller and weighted far harder to the back plane than the
         first pass. A handful of large flakes reads as blobs; many small
         distant ones with a few near reads as depth. Falls are long, because
         snow in still air drifts — it does not rain. */
      { z: 'far',  n: 0.62, s: [1.5, 3.5], fall: [26, 40], sway: [8, 14], amp: [5, 13] },
      { z: 'mid',  n: 0.28, s: [3, 5.5],   fall: [18, 27], sway: [6, 11], amp: [10, 22] },
      { z: 'near', n: 0.10, s: [6, 10],    fall: [12, 19], sway: [5, 9],  amp: [16, 34] }
    ];

    function build() {
      host.textContent = '';
      var w = window.innerWidth;
      /* ~1 flake per 26px of width, floored and capped so neither a 320px phone
         nor an ultrawide gets a silly number. */
      var total = Math.max(18, Math.min(52, Math.round(w / 28)));
      var rnd = function (a, b) { return a + Math.random() * (b - a); };

      for (var p = 0; p < PLANES.length; p++) {
        var pl = PLANES[p];
        var count = Math.round(total * pl.n);
        for (var i = 0; i < count; i++) {
          var f = document.createElement('span');
          f.className = 'sj-snow__f';
          f.setAttribute('data-z', pl.z);
          var fall = rnd(pl.fall[0], pl.fall[1]);
          f.style.setProperty('--x', rnd(-4, 104).toFixed(2) + 'vw');
          f.style.setProperty('--s', rnd(pl.s[0], pl.s[1]).toFixed(1) + 'px');
          f.style.setProperty('--fall', fall.toFixed(2) + 's');
          /* Negative delay starts each flake part-way down its own fall, so the
             first frame already looks like weather in progress instead of a
             batch released from the ceiling together. */
          f.style.setProperty('--delay', (-rnd(0, fall)).toFixed(2) + 's');
          var inner = document.createElement('i');
          inner.style.setProperty('--sway', rnd(pl.sway[0], pl.sway[1]).toFixed(2) + 's');
          inner.style.setProperty('--delay2', (-rnd(0, 8)).toFixed(2) + 's');
          inner.style.setProperty('--amp', rnd(pl.amp[0], pl.amp[1]).toFixed(1) + 'px');
          f.appendChild(inner);
          host.appendChild(f);
        }
      }
    }

    build();
    document.body.appendChild(host);

    /* Rebuild on a real width change only. Mobile Safari fires resize on every
       toolbar collapse, and rebuilding 70 nodes on scroll would be worse than
       the effect is worth. */
    var lastW = window.innerWidth, rt = null;
    window.addEventListener('resize', function () {
      if (Math.abs(window.innerWidth - lastW) < 80) return;
      lastW = window.innerWidth;
      window.clearTimeout(rt);
      rt = window.setTimeout(build, 220);
    }, { passive: true });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) { if (host.parentNode) host.parentNode.removeChild(host); }
      else if (!host.parentNode) { build(); document.body.appendChild(host); }
    });
  })();

})();
